/**
 * ReferentBeneficiairesView.jsx — Vue référent : liste + profil bénéficiaire.
 *
 * Architecture :
 *   - Sidebar (gauche)   : liste des bénéficiaires du référent (RBAC backend)
 *                          + bouton "Ajouter" (AddApprenantDialog auto-assigne referent_id)
 *   - Détail (droite)    : ApprenantCard (avec édition), entrée dispositif,
 *                          freins, documents, actions / RDV + envoi email + génération PDF.
 *
 * Sécurité : le backend (/bénéficiaires) renvoie déjà uniquement les bénéficiaires
 * dont referent_id === current_user.id pour un référent. Pas de filtrage côté front.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { FileText, Mail, Users, Clock, Search } from "lucide-react";
import { toast } from "sonner";

import { actionService } from "../../services/api";
import AddApprenantDialog from "../../components/AddApprenantDialog";
import AddActionDialog from "../../components/AddActionDialog";
import ApprenantCard from "../../components/ApprenantCard";
import EntreeDispositifCard from "../../components/EntreeDispositifCard";
import FreinsDetaillesModule from "../../components/FreinsDetaillesModule";
import DocumentUploader from "../../components/DocumentUploader";
import EmailComposeModal from "../../components/email/EmailComposeModal";

// ── Libellés des types d'action ───────────────────────────────────────────────
const TYPE_LABELS = {
  rdv: "Rendez-vous individuel",
  entretien: "Entretien de suivi",
  accompagnement: "Accompagnement",
  bilan: "Bilan",
  autre: "Autre",
};

// ── Styles des statuts de présence ────────────────────────────────────────────
const STATUT_STYLES = {
  non_defini: { className: "bg-slate-100 text-slate-600", label: "Planifié" },
  present: { className: "bg-emerald-100 text-emerald-700", label: "Présent" },
  absent: { className: "bg-red-100 text-red-700", label: "Absent" },
  absent_justifie: { className: "bg-amber-100 text-amber-700", label: "Abs. justifiée" },
  annule: { className: "bg-amber-100 text-amber-700", label: "Annulé" },
};
const getStatutStyle = (statut) =>
  STATUT_STYLES[statut] || STATUT_STYLES.non_defini;

// ── Format date FR ────────────────────────────────────────────────────────────
const fmtDate = (date) => {
  if (!date) return "";
  try {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return date;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────────────
export default function ReferentBeneficiairesView({
  beneficiaires = [],
  selectedBeneficiaire,
  setSelectedBeneficiaire,
  actions: actionsProp = [],
  onGeneratePDF,
  onRefresh,
}) {
  const [actions, setActions] = useState(actionsProp);
  const [loadingActions, setLoadingActions] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [search, setSearch] = useState("");

  // Synchronise avec les actions venant du parent
  useEffect(() => {
    setActions(actionsProp || []);
  }, [actionsProp]);

  // Recharge les actions (fallback local si le parent ne le fait pas)
  const loadActions = useCallback(async () => {
    if (!selectedBeneficiaire?.id) return;
    setLoadingActions(true);
    try {
      const response = await actionService.getAll(selectedBeneficiaire.id);
      setActions(response.data || []);
    } catch (err) {
      console.error("Erreur chargement actions :", err);
      toast.error("Impossible de charger les actions.");
    } finally {
      setLoadingActions(false);
    }
  }, [selectedBeneficiaire?.id]);

  // Filtre la liste par nom/prénom/email/zone
  const filteredBeneficiaires = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return beneficiaires;
    return beneficiaires.filter((b) => {
      const haystack = [
        b.prenom,
        b.nom,
        b.email,
        b.zone_geographique,
        b.ville,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [beneficiaires, search]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* ───────────────── Sidebar : liste des bénéficiaires ───────────────── */}
      <div className="lg:col-span-1">
        <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle
              className="text-xl font-bold"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Mes bénéficiaires
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({beneficiaires.length})
              </span>
            </CardTitle>
            <AddApprenantDialog onSuccess={onRefresh} />
          </CardHeader>
          <CardContent>
            {/* Recherche */}
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="pl-9 rounded-xl"
                data-testid="referent-beneficiaire-search"
              />
            </div>

            {/* Liste */}
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredBeneficiaires.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 text-sm">
                    {beneficiaires.length === 0
                      ? "Aucun bénéficiaire affecté pour le moment."
                      : "Aucun résultat."}
                  </p>
                </div>
              ) : (
                filteredBeneficiaires.map((b) => {
                  const isActif = b.statut === "actif";
                  const isEnAttente = b.statut === "en_attente_consentement";
                  const isRefus = b.statut === "refus_dispositif";
                  const isSelected = selectedBeneficiaire?.id === b.id;
                  return (
                    <div
                      key={b.id}
                      data-testid={`beneficiaire-item-${b.id}`}
                      onClick={() => setSelectedBeneficiaire(b)}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        isSelected
                          ? "bg-blue-50 border-2 border-blue-200"
                          : "bg-slate-50 border-2 border-transparent hover:border-blue-100"
                      }`}
                    >
                      <div className="font-bold text-slate-900">
                        {b.prenom} {b.nom}
                      </div>
                      <div className="text-sm text-slate-500">
                        {b.zone_geographique}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {isActif && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            Actif
                          </Badge>
                        )}
                        {isEnAttente && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            En attente consentement
                          </Badge>
                        )}
                        {isRefus && (
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            A refusé
                          </Badge>
                        )}
                        {b.actif === false && (
                          <Badge className="bg-slate-100 text-slate-600 text-xs">
                            Sorti
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ───────────────── Détail du bénéficiaire sélectionné ──────────────── */}
      <div className="lg:col-span-2">
        {selectedBeneficiaire ? (
          <div className="space-y-6">
            {/* Fiche bénéficiaire (avec bouton "Modifier" intégré) */}
            <ApprenantCard
              bénéficiaire={selectedBeneficiaire}
              onUpdate={onRefresh}
              userRole="referent"
            />

            {/* Consentement / entrée dans le dispositif */}
            <EntreeDispositifCard
              bénéficiaireId={selectedBeneficiaire.id}
              bénéficiaireEmail={selectedBeneficiaire.email}
              bénéficiaireNom={`${selectedBeneficiaire.prenom} ${selectedBeneficiaire.nom}`}
              onValidated={onRefresh}
            />

            {/* Freins détaillés */}
            <FreinsDetaillesModule bénéficiaireId={selectedBeneficiaire.id} />

            {/* Documents partagés */}
            <DocumentUploader bénéficiaireId={selectedBeneficiaire.id} />

            {/* Actions & RDV */}
            <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-4 flex-wrap gap-2">
                <CardTitle
                  className="text-xl font-bold"
                  style={{ fontFamily: "Nunito, sans-serif" }}
                >
                  Actions & RDV
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    ({actions.length})
                  </span>
                </CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => setShowEmailModal(true)}
                    variant="outline"
                    className="rounded-xl"
                    data-testid="referent-send-email-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer un email
                  </Button>
                  {onGeneratePDF && (
                    <Button
                      onClick={onGeneratePDF}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                      data-testid="referent-generate-pdf-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Générer PDF
                    </Button>
                  )}
                  <AddActionDialog
                    bénéficiaireId={selectedBeneficiaire.id}
                    onSuccess={loadActions}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {loadingActions ? (
                  <p className="text-center py-8 text-slate-400">
                    Chargement des actions…
                  </p>
                ) : actions.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500 text-sm">
                      Aucune action enregistrée pour ce bénéficiaire.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {actions.map((action) => {
                      const s = getStatutStyle(action.statut_presence);
                      const typeLabel =
                        TYPE_LABELS[action.type_action] ||
                        action.type_action ||
                        "Action";
                      return (
                        <div
                          key={action.id}
                          className="p-4 rounded-xl border border-slate-200 bg-slate-50"
                          data-testid={`referent-action-${action.id}`}
                        >
                          <div className="flex justify-between items-start gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-slate-900">
                                  {action.titre || typeLabel}
                                </span>
                                <Badge className={`${s.className} text-xs`}>
                                  {s.label}
                                </Badge>
                                <span className="text-xs text-slate-500">
                                  {typeLabel}
                                </span>
                              </div>
                              <div className="text-sm text-slate-600 mt-1">
                                {fmtDate(action.date)}
                                {action.heure_debut && (
                                  <span className="ml-2">
                                    • {action.heure_debut}
                                    {action.heure_fin
                                      ? ` – ${action.heure_fin}`
                                      : ""}
                                  </span>
                                )}
                              </div>
                              {action.notes && (
                                <p className="text-sm text-slate-500 mt-2">
                                  {action.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="bg-white border-slate-200 rounded-3xl p-12 shadow-sm">
            <div className="text-center">
              <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-bold text-slate-400 mb-2">
                Sélectionnez un bénéficiaire
              </h3>
              <p className="text-slate-400">
                Cliquez sur un bénéficiaire dans la liste à gauche pour afficher
                son suivi.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Modal email */}
      {showEmailModal && selectedBeneficiaire && (
        <EmailComposeModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          defaultTo={selectedBeneficiaire.email || ""}
          defaultSubject="Suivi de votre accompagnement — InserPro"
          beneficiaireId={selectedBeneficiaire.id}
        />
      )}
    </div>
  );
}
